// @fileoverview : 영업관리 > 수주항목
import { mutatePurchaseOrderItems } from "@/api/purchaseOrderItems/usePurchaseOrderItems";
import { users } from "@/api/users/useUsersQuery";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { PartnerDetailForm } from "@/features/partner/form/detailForm";
import { ProductionPlanCreateForm } from "@/features/productionPlan/form/CreateForm";
import { DeliveryForm } from "@/features/purchaseOrder/form/deliveryForm";
import { PurchaseOrderDetailForm } from "@/features/purchaseOrder/form/detailForm";
import { DeliveredForm } from "@/features/purchaseOrderItems/delivered/deliveredForm";
import { ArrivalDeliveryStockPlanForm } from "@/features/purchaseOrderItems/form/arrivalDeliveryStockPlanForm";
import { PurchaseOrderItemsCreateForm } from "@/features/purchaseOrderItems/form/createForm";
import { PurchaseOrderItemsUpdateForm } from "@/features/purchaseOrderItems/form/updateForm";
import { ProductionPlanForm } from "@/features/purchaseOrderItems/productionPlan/productionPlanForm";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/alert";
import { ConfirmForm } from "@/features/ui/confirm";
import { UsersDetailForm } from "@/features/users/components/form/UsersDetailForm";
import DatetimeUtil from "@/utils/dateTimeUtil";
import { handleErrorResponse } from "@/utils/errorMessage";
import timeUtil from "@/utils/timeUtil";
import { ActionIcon, Avatar, Button, Flex, Text, Tooltip } from "@mantine/core";
import {
  AuthSignupPost201ResponseEquipmentPurchaseOrderItem,
  PurchaseOrderItemsGet200Response,
  ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconFilterPlus, IconPlus, IconRecycle, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

export const PurchaseOrderItemsPage = () => {
  const { t } = useTranslation();
  const { data: userData } = useQuery(users.who({}));
  const userCode = userData?.data.code;
  const orderTitle = userCode === "PB" ? t("order.order") : t("purchaseOrder.purchaseOrder"); // TODO: 다국어 처리

  const { openModal, closeModal } = useModal();
  const { query, setQuery, selectedRows, refetch, resetQueryStrings } = useStandardLayout();

  useEffect(() => {
    if (query.length === 0) {
      // query가 비어 있을 때만 실행
      setQuery([
        { direction: { $eq: "INBOUND" } },
        {
          $or: [
            { creatorUserCode: userCode },
            { creatorUserCode: "PB" },
            { partnerCode: userCode },
          ],
        },
      ]);
    }
  }, [query, userCode]); // 의존성 배열 유지

  // delete 및 선택 테스트 다 해봐야됨.
  const { mutate: deleteMutate } = useMutation(
    (params: ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest) =>
      mutatePurchaseOrderItems
        .delete(params)
        .mutationFn(params as ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest | any),
    {
      onSuccess: () => {
        refetch();
        customAlert(
          `${t("message.deleteSuccess", { item: `${orderTitle} 항목` })}`,
          `${t("common.deleteSuccess")}`,
          "green",
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({ error, errorContext: `${orderTitle}${t("purchaseOrder.id")}` }),
          `${t("common.deleteFail", { item: `${orderTitle} 항목` })}`,
          "red",
        );
      },
    },
  );

  const columns: readonly Column<AuthSignupPost201ResponseEquipmentPurchaseOrderItem>[] = [
    {
      ...SelectColumn,
      resizable: true,
    },
    {
      key: "partner.name",
      name: "협력사명",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <PartnerDetailForm partnerCode={formatterProps?.row.partnerCode} />,
                null,
                "협력사 상세",
              )
            }
          >
            {formatterProps.row.partner?.name}
          </LinkAnchor>
        );
      },
    },
    {
      key: "purchaseOrderId",
      name: `${orderTitle}번호`,
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex w="100%" h="100%" justify="flex-end">
            <LinkAnchor
              onClick={() =>
                openModal(
                  <PurchaseOrderDetailForm
                    purchaseOrderId={formatterProps?.row.purchaseOrderId ?? 0}
                  />,
                  null,
                  `${orderTitle} 상세`,
                )
              }
            >
              {formatterProps.row.purchaseOrderId}
            </LinkAnchor>
          </Flex>
        );
      },
    },
    {
      key: "itemName",
      name: "품목",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <ItemsDetailForm itemCode={formatterProps?.row?.itemCode} />,
                null,
                "협력사 상세",
              )
            }
          >
            {formatterProps.row.itemName}
          </LinkAnchor>
        );
      },
    },
    {
      key: "quantity",
      name: "수량",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return <Flex justify="flex-end">{formatterProps.row.quantity}</Flex>;
      },
    },
    {
      key: "sumOfProductionPlanQuantity",
      name: "생산 계획된 수량",
      sortable: true,
      resizable: true,

      formatter: (formatterProps) => {
        return (
          <Flex justify="flex-end">
            {formatterProps.row.sumOfProductionPlanQuantity === "0" ? (
              <div>{formatterProps.row.sumOfProductionPlanQuantity}</div>
            ) : (
              <LinkAnchor
                onClick={() =>
                  openModal(
                    <ProductionPlanForm purchaseOrderItemID={formatterProps?.row?.id} />,
                    null,
                    "생산계획 상세",
                  )
                }
              >
                {formatterProps.row.sumOfProductionPlanQuantity}
              </LinkAnchor>
            )}
          </Flex>
        );
      },
    },
    {
      key: "sumOfStockPlanQuantity",
      name: "납품 계획된 수량",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return <Flex justify="flex-end">{formatterProps.row.sumOfStockPlanQuantity}</Flex>;
      },
    },
    {
      key: "sumOfDeliveredQuantity",
      name: "납품 완료 수량",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex h="100%" justify="flex-end" align="center">
            {formatterProps.row.sumOfDeliveredQuantity === "0" ? (
              <Text>{formatterProps.row.sumOfDeliveredQuantity}</Text>
            ) : (
              <LinkAnchor
                onClick={() =>
                  openModal(
                    <DeliveredForm purchaseOrderItemID={formatterProps?.row?.id} />,
                    null,
                    "납품 완료상세",
                  )
                }
              >
                {formatterProps.row.sumOfDeliveredQuantity}
              </LinkAnchor>
            )}
          </Flex>
        );
      },
    },
    {
      key: "manager",
      name: "담당자 명",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <UsersDetailForm UserCode={formatterProps?.row?.userCode} />,
                null,
                "사용자 상세",
              )
            }
          >
            {formatterProps.row.manager}
          </LinkAnchor>
        );
      },
    },
    {
      key: "createdAt",
      name: "접수일",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex>
            {formatterProps.row.createdAt !== null
              ? DatetimeUtil(formatterProps.row.createdAt as string)
              : ""}
          </Flex>
        );
      },
    },
    {
      key: "deliveryDeadline",
      name: "납기일",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex>
            {formatterProps.row.deliveryDeadline !== null
              ? timeUtil(formatterProps.row.deliveryDeadline as string)
              : ""}
          </Flex>
        );
      },
    },
    {
      key: "options",
      name: "액션",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        const updatePurchaseOrderItemActionView = () => {
          openModal(
            <PurchaseOrderItemsUpdateForm formatterProps={formatterProps.row} />,
            null,
            "수주 항목 수정",
          );
        };

        const deletePurchaseOrderItemAction = () => {
          openModal(
            <ConfirmForm
              message="정말로 삭제하시겠습니까?"
              yesCallback={() => {
                deleteMutate({ purchaseOrderItemId: formatterProps?.row?.id as number });
              }}
              noCallback={() => {
                closeModal(null);
              }}
            />,
            null,
            "수주 항목 삭제",
          );
        };

        return (
          <Flex w="100%" h="100%" justify="center" align="center" gap="md">
            <ActionIcon variant="subtle" onClick={updatePurchaseOrderItemActionView} color="yellow">
              <IconEdit />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              onClick={() => deletePurchaseOrderItemAction()}
              color="red"
            >
              <IconTrash />
            </ActionIcon>
          </Flex>
        );
      },
    },
  ];

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert(
        `${t("message.deleteSelect", { item: `${t("purchaseOrder.purchaseOrder")}` })}`,
        `${t("common.deleteFail", { item: `${t("purchaseOrder.purchaseOrder")}` })}`,
        "red",
      );
      return;
    }

    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ purchaseOrderItemId: row });
          });

          selectedRows.clear();
        }}
        noCallback={() => {}}
        commonCallback={() => closeModal(null)}
      />,
      null,
      `${t("common.delete", { item: `${orderTitle}` })}`,
    );
  };

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex p="sm" justify="space-between" rowGap="md">
        <Flex gap="sm">
          <Button
            leftIcon={<IconPlus />}
            onClick={() =>
              openModal(<PurchaseOrderItemsCreateForm />, null, `${orderTitle} 항목 추가`)
            }
          >
            추가
          </Button>
          <Button
            leftIcon={<IconPlus />}
            color="orange"
            disabled={[...selectedRows].length !== 1}
            onClick={() =>
              openModal(
                <DeliveryForm
                  formatterProps={
                    JSON.parse(
                      [...selectedRows].toString(),
                    ) as AuthSignupPost201ResponseEquipmentPurchaseOrderItem
                  }
                />,
                null,
                "납품하기",
              )
            }
          >
            납품하기
          </Button>
          <Button
            leftIcon={<IconPlus />}
            color="green"
            disabled={[...selectedRows].length !== 1}
            onClick={() =>
              openModal(
                <ArrivalDeliveryStockPlanForm
                  formatterProps={
                    JSON.parse(
                      [...selectedRows].toString(),
                    ) as AuthSignupPost201ResponseEquipmentPurchaseOrderItem
                  }
                  purchaseOrder={true}
                />,
                null,
                "출고계획",
                true,
              )
            }
          >
            출고계획 작성
          </Button>
          <Button
            leftIcon={<IconPlus />}
            color="orange"
            disabled={[...selectedRows].length !== 1}
            onClick={() =>
              openModal(
                <ProductionPlanCreateForm
                  formatterProps={
                    JSON.parse(
                      [...selectedRows].toString(),
                    ) as AuthSignupPost201ResponseEquipmentPurchaseOrderItem
                  }
                />,
                null,
                "생산계획 작성",
                true,
              )
            }
          >
            생산계획 작성
          </Button>
        </Flex>
        <Flex gap="sm">
          <Button rightIcon={<IconRecycle />} color="teal" onClick={() => resetQueryStrings()}>
            {t("common.reset")}
          </Button>
          <Button rightIcon={<IconTrash />} color="red" onClick={() => handleDelete()}>
            {t("common.rowDelete")}
          </Button>
        </Flex>
      </Flex>
      <Flex bg="white" px="sm" direction="column" gap="xs">
        <Text fz="xl" fw={600}>
          {t("common.list", { item: `${orderTitle} 항목` })}
        </Text>
        <Text fz="xs">
          {t("common.subTitle", { item: `${orderTitle} 항목` })} (품목별 고객 주문건)
        </Text>
      </Flex>
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<
            PurchaseOrderItemsGet200Response,
            AuthSignupPost201ResponseEquipmentPurchaseOrderItem
          >
            columns={columns}
            rowKeyGetter={(row: AuthSignupPost201ResponseEquipmentPurchaseOrderItem) =>
              JSON.stringify(row)
            }
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<PurchaseOrderItemsGet200Response> />
      </Flex>
    </Flex>
  );
};
